import {
  useCallback, useContext, useEffect, useMemo, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { useAuthenticationPreflight } from "Components/Generic/Authentication";
import { selectSelectedOrActiveProjects } from "Store/Selectors/Projects";
import { getMissingFieldList, getHasRoles } from "Utils/Project";
import { projectsExportDialog, projectsExportStart } from "Store/Actions";
import { selectExportIndex, selectShowExportDialog } from "Store/Selectors/Export";
import { ConfigurationContext } from "Components/Generic/Configuration";
import { MissingFields } from "Model/MissingFields";
import { hasNoElements } from "Utils/Array";
import { IUseExport } from "./IUseExport";
import { ExportStage } from "./ExportStage";

export function UseExport(): IUseExport {
  const dispatch = useDispatch();
  const { preflightAuthentication } = useAuthenticationPreflight();
  const config = useContext(ConfigurationContext);

  const selectedProjects = useSelector(selectSelectedOrActiveProjects);
  const showExportDialog = useSelector(selectShowExportDialog);
  const exportIndex = useSelector(selectExportIndex);

  const [stage, setStage] = useState<ExportStage>(ExportStage.Initial);

  useEffect(() => {
    if (showExportDialog && stage === ExportStage.Initial) {
      setStage(ExportStage.CheckingRoles);
    } else if (exportIndex === -1) {
      setStage(ExportStage.Initial);
    }
  }, [exportIndex, showExportDialog, stage]);

  // #region MISSING_ROLES
  const hideMissingRolesDialog = useMemo(() => {
    if (stage !== ExportStage.CheckingRoles) {
      return true;
    }

    if (getHasRoles(selectedProjects)) {
      setStage(ExportStage.CheckingFields);
      return true;
    }

    return false;
  }, [selectedProjects, stage]);

  const onConfirmMissingRolesDialog = useCallback(() => {
    setStage(ExportStage.CheckingFields);
  }, []);

  const onDismissMissingRolesDialog = useCallback(() => {
    setStage(ExportStage.Initial);
    dispatch(projectsExportDialog(false));
  }, [dispatch]);

  // #endregion

  // #region MISSING_FIELDS
  const [missingFieldsList, setMissingFieldsList] = useState<MissingFields[]>(null);
  const hideRequiredFieldsDialog = useMemo(() => {
    if (stage !== ExportStage.CheckingFields) {
      return true;
    }

    const missingFields = getMissingFieldList(selectedProjects, config);

    if (hasNoElements(missingFields)) {
      setStage(ExportStage.Export);
      return true;
    }

    setMissingFieldsList(missingFields);
    return false;
  }, [config, selectedProjects, stage]);

  const onConfirmRequiredFieldsDialog = useCallback(() => {
    setStage(ExportStage.Export);
  }, []);

  const onDismissRequiredFieldsDialog = useCallback(() => {
    setStage(ExportStage.Initial);
    dispatch(projectsExportDialog(false));
  }, [dispatch]);

  // #endregion

  // #region EXPORT
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!showExportDialog) {
      setTimeout(() => {
        setStage(ExportStage.Initial);
        setIsOpen(false);
      }, 250);
      return;
    }

    if (stage === ExportStage.Export) {
      (async () => {
        await preflightAuthentication();
        setIsOpen(true);
        dispatch(projectsExportStart());
      })();
    }
  }, [dispatch, preflightAuthentication, showExportDialog, stage]);

  const onDismissExport = useCallback(() => {
    dispatch(projectsExportDialog(false));
  }, [dispatch]);
  // #endregion

  const hasExportableProjects = (missingFieldsList?.length ?? 0) < selectedProjects.length;

  return {
    hideRequiredFieldsDialog,
    hideMissingRolesDialog,
    onConfirmRequiredFieldsDialog: hasExportableProjects ? onConfirmRequiredFieldsDialog : null,
    onConfirmMissingRolesDialog,
    onDismissExport,
    onDismissRequiredFieldsDialog,
    onDismissMissingRolesDialog,
    showExportDialog: isOpen,
    missingFieldsList
  };
}
